import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoaderIcon, MessageSquareIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getApiEndpoint } from "@/lib/api";
import { getToken } from "@/lib/auth-storage";

interface ChatRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChatStarted: (chatId: number) => void;
}

const ChatRequestDialog = ({ open, onOpenChange, onChatStarted }: ChatRequestDialogProps) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasActiveChat, setHasActiveChat] = useState(false);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      checkChatStatus();
    }
  }, [open]);

  const checkChatStatus = async () => {
    try {
      setChecking(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/chat/status"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_active_chat && data.active_chat) {
          setHasActiveChat(true);
          setActiveChatId(data.active_chat.id);
        } else {
          setHasActiveChat(false);
        }
      }
    } catch (error) {
      console.error("Erro ao verificar status do chat:", error);
    } finally {
      setChecking(false);
    }
  };

  const handleRequestChat = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/chat/request"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: message || "Solicito contato com um administrador",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Solicitação enviada",
          description: "Um administrador será notificado. Aguarde a aceitação.",
        });
        onOpenChange(false);
        setMessage("");
        // Verificar status periodicamente
        startPolling();
      } else {
        if (data.chat_id) {
          // Já existe um chat ativo
          setHasActiveChat(true);
          setActiveChatId(data.chat_id);
          onChatStarted(data.chat_id);
          onOpenChange(false);
        } else {
          toast({
            title: "Erro",
            description: data.error || "Não foi possível criar a solicitação",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível criar a solicitação",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    const interval = setInterval(async () => {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/chat/status"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_active_chat && data.active_chat) {
          clearInterval(interval);
          setHasActiveChat(true);
          setActiveChatId(data.active_chat.id);
          onChatStarted(data.active_chat.id);
        }
      }
    }, 3000); // Verificar a cada 3 segundos

    // Limpar após 5 minutos
    setTimeout(() => clearInterval(interval), 5 * 60 * 1000);
  };

  const handleOpenChat = () => {
    if (activeChatId) {
      onChatStarted(activeChatId);
      onOpenChange(false);
    }
  };

  if (checking) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <LoaderIcon className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (hasActiveChat) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareIcon className="h-5 w-5 text-blue-600" />
              Chat Disponível
            </DialogTitle>
            <DialogDescription>
              Você já possui um chat ativo com um administrador.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button onClick={handleOpenChat} className="bg-blue-600 hover:bg-blue-700">
              Abrir Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareIcon className="h-5 w-5 text-blue-600" />
            Solicitar Chat com Administrador
          </DialogTitle>
          <DialogDescription>
            Envie uma solicitação para conversar com um administrador. Você será notificado quando sua solicitação for aceita.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Mensagem inicial (opcional)
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Descreva brevemente o motivo do contato..."
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleRequestChat}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              "Enviar Solicitação"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChatRequestDialog;



